import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { IUserData } from 'src/app/auth/models/user-data';
import { UserRole } from 'src/app/auth/models/user-roles';
import { AuthService } from 'src/app/auth/services/auth.service';
import { FeatureFlag } from 'src/app/feature-flag/feature-flag.type';
import { FeatureFlagService } from 'src/app/feature-flag/services/feature-flag.service';
import { checkEntityIdEquality } from 'src/app/shared/lib/utilities';
import { EntityId } from 'src/app/shared/models/common';
import { ITenantDTO } from 'src/app/shared/models/tenant';
import { environment } from 'src/environments/environment';
import { AnnouncekitComponent } from 'announcekit-angular';
import { TenantService } from 'src/app/tenant/services/tenant.service';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NavigationComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly tenantService = inject(TenantService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private changeRef = inject(ChangeDetectorRef);

  @ViewChild('announceKitTemplate', { static: false })
  announceKitTemplate!: AnnouncekitComponent;

  menuIsOpen = false;

  checkEntityIdEquality = checkEntityIdEquality;

  userData?: IUserData;
  user!: {
    name?: string;
    email?: string;
    id: string;
  };

  readonly inDevelopment = !environment.production;

  readonly isUserLoggedIn$ = this.authService.isAuthenticated$;
  currentTenant?: ITenantDTO;

  readonly currentTenantHelpPortalUrl = this.tenantService.helpPortalUrl;

  appHeaderAlias = computed(() => this.tenantService.appHeaderAlias());

  // TODO: Implement favourite tenant functionality
  // favouriteTenantId = this.tenantService.favouriteTenantId ?? '';

  selectedTenantId = computed(() => this.tenantService.tenantDetails()?.id);

  readonly UserRole = UserRole;

  // NOTE: This should probably be moved out into the same file as feature flag definitions
  // Would be another good use case of having a value with a string display property.
  readonly featureFlags = [
    {
      name: 'Full locker audit fields',
      value: FeatureFlag.ShowAllLockerAuditFields,
    },
    {
      name: 'Full locker status fields',
      value: FeatureFlag.ShowAllLockerStatusFields,
    },
    {
      name: 'Disable creating shift locker banks',
      value: FeatureFlag.DisableShiftLockerBanks,
    },
    {
      name: 'New UI',
      value: FeatureFlag.UseNewAdminLayout,
    },
  ];

  ngOnInit(): void {
    this.authService.userData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userData) => {
        // TODO build a favourite tenant feature
        
        this.userData = userData;
        this.user = {
          name: this.userData?.name,
          email: this.userData?.email,
          id: this.userData?.email || '',
        };
      });
  }

  loginUser() {
    this.authService.login();
  }

  logoutUser() {
    this.authService.logoffAndRevokeTokens();
  }

  toggleRole(role: UserRole) {
    this.authService.toggleRoleEnabled(role);
  }

  hasRole(role: UserRole): boolean {
    return this.userData?.roles?.includes(role) ?? false;
  }

  isRoleEnabled(role: UserRole) {
    return this.authService.userHasRoleAndRoleIsEnabled(role);
  }

  toggleFeatureFlag(flag: FeatureFlag) {
    this.featureFlagService.toggleFeatureFlag(flag);
    
    // Reload the page if toggling the admin layout flag
    if (flag === FeatureFlag.UseNewAdminLayout) {
      const currentUrl = this.router.url;
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(currentUrl);
      });
    }
  }

  isFeatureFlagSet(flag: FeatureFlag) {
    return this.featureFlagService.hasFeatureFlagSet(flag);
  }

  menuOpened() {
    this.menuIsOpen = true;
  }

  menuClosed() {
    this.menuIsOpen = false;
  }

  getAdminRoute(): string {
    const tenantCode = this.tenantService.tenantCode();
    return tenantCode ? `/${tenantCode}/admin` : '/admin';
  }
}
